import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import { apiRequest } from '../../config/api';

type LegalSection = 'legal' | 'terms' | 'privacy' | 'compliance' | 'disclaimer';

const sectionKeyMap: Record<LegalSection, string> = {
  legal: 'legal',
  terms: 'service',
  privacy: 'privacy',
  compliance: 'compliance',
  disclaimer: 'disclaimer',
};

export default function LegalPage() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<LegalSection>('legal');
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const menuItems = [
    { id: 'legal' as LegalSection, label: t('legal.menuItems.legal'), icon: 'ri-scales-3-line' },
    { id: 'terms' as LegalSection, label: t('legal.menuItems.terms'), icon: 'ri-file-text-line' },
    { id: 'privacy' as LegalSection, label: t('legal.menuItems.privacy'), icon: 'ri-shield-check-line' },
    { id: 'compliance' as LegalSection, label: t('legal.menuItems.compliance'), icon: 'ri-checkbox-circle-line' },
    { id: 'disclaimer' as LegalSection, label: t('legal.menuItems.disclaimer'), icon: 'ri-error-warning-line' },
  ];

  const fetchContent = async (section: LegalSection) => {
    setLoading(true);
    setError('');
    
    try {
      const key = sectionKeyMap[section];
      const response = await apiRequest<{
        code: number;
        msg: string;
        data: {
          setting: {
            content: string;
            file: string | null;
            key: string;
          };
        };
      }>('setting', {
        method: 'POST',
        body: { key },
      });

      if (response.code === 0 && response.data?.setting?.content) {
        setContent(response.data.setting.content);
      } else {
        setError(t('legal.noContent'));
        setContent('');
      }
    } catch (err) {
      setError(t('legal.error'));
      setContent('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent(activeSection);
  }, [activeSection]);

  const getCurrentTitle = () => {
    const item = menuItems.find((item) => item.id === activeSection);
    return item?.label || '';
  };

  return (
    <div className="min-h-screen bg-neutral-bg">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-8">
            {/* Left Sidebar Navigation */}
            <aside className="w-72 flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
                <h2 className="text-xl font-bold text-neutral-dark mb-6">{t('legal.title')}</h2>
                <nav className="space-y-2">
                  {menuItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full px-4 py-3 rounded-xl flex items-center gap-3 transition-all cursor-pointer whitespace-nowrap ${
                        activeSection === item.id
                          ? 'bg-gradient-primary text-white shadow-md'
                          : 'text-neutral-text hover:bg-neutral-bg hover:text-neutral-dark'
                      }`}
                    >
                      <i className={`${item.icon} text-xl w-6 h-6 flex items-center justify-center`}></i>
                      <span className="font-medium">{item.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Right Content Area */}
            <div className="flex-1">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h1 className="text-3xl font-bold text-neutral-dark mb-6">{getCurrentTitle()}</h1>
                
                {loading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3 text-neutral-text">
                      <i className="ri-loader-4-line text-2xl animate-spin"></i>
                      <span>{t('legal.loading')}</span>
                    </div>
                  </div>
                )}

                {error && !loading && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                    <i className="ri-error-warning-line text-3xl text-red-500 mb-2"></i>
                    <p className="text-red-600">{error}</p>
                  </div>
                )}

                {!loading && !error && content && (
                  <div 
                    className="prose max-w-none text-neutral-text leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                )}

                {!loading && !error && !content && (
                  <div className="bg-neutral-bg rounded-xl p-12 text-center">
                    <i className="ri-file-text-line text-5xl text-neutral-text/30 mb-4"></i>
                    <p className="text-neutral-text">{t('legal.noContent')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
